// Every game-feel number in one place. Distances are world units measured
// from the player (who stands at 0); times are seconds of sim time.

/** World scale: distances and walk speeds both scale by this, so timings are
 *  unchanged while fighters fill more of a portrait screen. */
const K = 0.72;

export const TUNING = {
  /** Fixed sim step. Inputs are applied between steps at their own timestamp. */
  dt: 1 / 240,

  // Distance (maai)
  reach: 1.2 * K, // the front enemy can be struck at or inside this distance
  attackRange: 0.8 * K, // enemies stop here and wind up their attack
  spawnDistance: 3.9 * K, // just past the visible mat (±2.5) on a portrait phone
  queueSpacing: 0.55 * K, // gap kept behind the enemy in front on the same side

  // Timing windows, converted to distance using each enemy's walk speed
  perfectWindow: 0.1, // a hit this soon after the enemy enters reach is PERFECT
  earlyGrace: 0.06, // a tap this far ahead of the enemy entering reach still connects

  // Player
  hearts: 3,
  strikeTime: 0.12, // strike pose; the player can strike again right away
  whiffRecover: 0.2, // a strike at nothing leaves the player open this long
  inputBuffer: 0.2, // a tap during that lockout fires when it ends, if a target is in range
  hurtInvuln: 1.0,
  hurtPushTo: 1.6 * K, // pushed back to this distance when the player takes a hit

  // Reactions
  knockback: 1.0 * K, // a non-lethal hit pushes the enemy back this far
  blockPush: 0.6 * K,
  recoilSpeed: 5.0 * K,
  deadTime: 0.95, // knocked-down body stays in the list for the renderer

  // Kiai
  kiaiPerPerfect: 0.125,
  kiaiPerCounter: 0.125,
  kiaiPerGood: 0.04,
  kiaiSlowmo: 0.9, // real seconds of slow motion after a kiai
  kiaiTimeScale: 0.3,

  // Scoring
  points: { perfect: 100, good: 50, counter: 150, kiai: 100 },
  comboStep: 10, // multiplier goes up by 1 every this many hits in a row
  comboMaxMult: 5,
  /** PERFECT chain: unbroken PERFECT/COUNTER hits. Every `step` of them is a tier;
   *  each tier adds `bonus` points (× combo multiplier) and `kiai` meter per hit. */
  chain: { step: 5, bonus: 25, kiai: 0.02, maxTier: 4 },

  // Difficulty ramps with kills: level = floor(kills / killsPerLevel)
  killsPerLevel: 6,
  walkSpeed: { base: 1.7 * K, perLevel: 0.12 * K, max: 3.6 * K },
  arrivalGap: { base: 1.15, perLevel: 0.06, min: 0.36, jitter: 0.35 },
  /** Past OVERTIME_FROM (only Kumite climbs that high) it keeps getting harder,
   *  ever more slowly: windups shorten toward `windup` of their length, the
   *  arrival gap toward `gap` seconds, walking gets up to `speed` faster, and
   *  the plain street fighter thins out toward `basic` of his share, leaving
   *  the trickier kinds, whose extra arrival time shrinks toward `extra` of
   *  itself. Each level `rate` of what's left is still to come. */
  overtime: { rate: 0.96, windup: 0.35, gap: 0.14, speed: 0.35, basic: 0.5, extra: 0.15 },
  maxSameSide: 3,
} as const;

export type EnemyKind = 'basic' | 'heavy' | 'feinter' | 'blocker' | 'kicker' | 'grappler';

export interface KindDef {
  hp: number;
  speedMult: number;
  windup: number; // telegraph length before the attack lands
  /** Kinds join the mix at this level, then their weight ramps in. */
  fromLevel: number;
  weight: number;
  /** Extra arrival time budgeted after this kind so its pattern fits. */
  extraGap: number;
}

export const KINDS: Record<EnemyKind, KindDef> = {
  basic: { hp: 1, speedMult: 1, windup: 0.5, fromLevel: 0, weight: 5, extraGap: 0 },
  heavy: { hp: 2, speedMult: 0.8, windup: 0.65, fromLevel: 1, weight: 2, extraGap: 0.55 },
  feinter: { hp: 1, speedMult: 1.05, windup: 0.45, fromLevel: 2, weight: 2, extraGap: 0.5 },
  blocker: { hp: 1, speedMult: 0.9, windup: 0.55, fromLevel: 3, weight: 2, extraGap: 0.6 },
  kicker: { hp: 1, speedMult: 1.15, windup: 0.5, fromLevel: 5, weight: 2, extraGap: 0.7 },
  grappler: { hp: 2, speedMult: 1.0, windup: 0.4, fromLevel: 7, weight: 2, extraGap: 0.8 },
};

/**
 * Kicker: chambers its kick this far outside reach (a strike there whiffs),
 * then launches in at `launchSpeed`; the kick lands on reaching attackRange.
 * The launch through reach is the window to counter it.
 */
export const KICK = { stand: 0.32 * K, launchSpeed: 2.4 * K } as const;

/**
 * Grappler: shrugs off a hit (a flinch that keeps it in reach) and keeps pressing in,
 * slowed inside reach, so the counter is two quick strikes before it grabs.
 * The grab is its windup at attackRange; if that completes, it throws.
 */
export const GRAPPLE = { shrug: 0.03 * K, press: 0.55 } as const;

/** Feinter: stops this far outside reach, fakes a lunge, then rushes in. */
export const FEINT = { margin: 0.4 * K, duration: 0.5, rushMult: 1.6 } as const;

/**
 * How each kind moves in: a speed multiplier over walking time that pulses
 * like real footwork (surge, settle) and averages 1, so arrival timing and
 * the difficulty curve are unchanged.
 */
export const GAIT: Record<EnemyKind, { hz: number; depth: number; stomp?: boolean }> = {
  basic: { hz: 1.7, depth: 0.45 }, // stalking, lunging steps
  heavy: { hz: 1.1, depth: 0.6, stomp: true }, // heavy surges between stomps
  feinter: { hz: 2.4, depth: 0.3 }, // light, bouncy boxer's steps
  blocker: { hz: 1.9, depth: 0.4 }, // pressing forward behind the guard
  kicker: { hz: 2.6, depth: 0.35 }, // springy kickboxer's bounce
  grappler: { hz: 1.4, depth: 0.35 }, // low, shuffling pressure
};

/** The first level where walk speed and the arrival gap have both reached their
 *  caps: overtime takes over from there, so the curve never stalls or doubles up. */
export const OVERTIME_FROM = Math.ceil(
  Math.max(
    (TUNING.walkSpeed.max - TUNING.walkSpeed.base) / TUNING.walkSpeed.perLevel,
    (TUNING.arrivalGap.base - TUNING.arrivalGap.min) / TUNING.arrivalGap.perLevel,
  ),
);

/** How far past OVERTIME_FROM a level is: 0 up to it, rising toward 1. */
export function overtimeFor(level: number): number {
  const o = level - OVERTIME_FROM;
  return o > 0 ? 1 - TUNING.overtime.rate ** o : 0;
}

/** `x` at a level, moved toward `target` as far as overtime has come. */
export function toward(x: number, target: number, level: number): number {
  return x + (target - x) * overtimeFor(level);
}

/** A kind's windup (s) at a level: shorter in overtime, so the tells come faster. */
export function windupFor(kind: EnemyKind, level: number): number {
  const w = KINDS[kind].windup;
  return toward(w, w * TUNING.overtime.windup, level);
}

export function gait(kind: EnemyKind, t: number): number {
  const g = GAIT[kind];
  const s = Math.sin(t * Math.PI * 2 * g.hz);
  // Stomp: all the motion in the push-off half of the step (mean of |sin| is 2/π)
  return g.stomp ? 1 - g.depth + g.depth * (Math.PI / 2) * Math.abs(s) : 1 + g.depth * s;
}

/**
 * Attack patterns: short authored sequences the spawner can send instead of a
 * single opponent (src/sim/patterns.ts plans them). Steps are in the order
 * the player should strike: [kind, or null for any one-hit kind in the mix;
 * side 0 or 1 (mirrored at random); beats after the previous step's strike,
 * 0 = both at once, `PATTERN.pincerGap` apart]. A pattern can come up from
 * `fromLevel` (to `toLevel`) when the round's mix has its kinds.
 */
export type PatternId =
  | 'pingPong'
  | 'train'
  | 'pincer'
  | 'feintBait'
  | 'shield'
  | 'guardFirst'
  | 'rush'
  | 'kickCover'
  | 'grappleCover';

export type PatternStep = readonly [kind: EnemyKind | null, side: 0 | 1, beats: number];

export interface PatternDef {
  steps: readonly PatternStep[];
  fromLevel: number;
  toLevel?: number;
  weight: number;
  /** Extra beats of quiet after the pattern. */
  rest: number;
}

export const PATTERNS: Record<PatternId, PatternDef> = {
  // Left, right, left, right on the beat
  pingPong: {
    steps: [
      [null, 0, 0],
      [null, 1, 1],
      [null, 0, 1],
      [null, 1, 1],
    ],
    fromLevel: 0,
    weight: 3,
    rest: 1,
  },
  // Three from one side, a beat apart
  train: {
    steps: [
      ['basic', 0, 0],
      ['basic', 0, 1],
      ['basic', 0, 1],
    ],
    fromLevel: 1,
    toLevel: 8,
    weight: 2,
    rest: 1,
  },
  // Both sides at once
  pincer: {
    steps: [
      ['basic', 0, 0],
      ['basic', 1, 0],
    ],
    fromLevel: 3,
    weight: 2,
    rest: 1,
  },
  // A fake on one side while the real attack comes from the other
  feintBait: {
    steps: [
      ['basic', 1, 0],
      ['feinter', 0, 0.5],
    ],
    fromLevel: 3,
    weight: 2,
    rest: 1,
  },
  // A heavy in front, a feinter behind him
  shield: {
    steps: [
      ['heavy', 0, 0],
      ['feinter', 0, 0.5],
    ],
    fromLevel: 4,
    weight: 1,
    rest: 1,
  },
  // A blocker already guarding while an attacker comes from the other side:
  // strike the attacker, then counter the blocker
  guardFirst: {
    steps: [
      ['basic', 1, 0],
      ['blocker', 0, 0.5],
    ],
    fromLevel: 5,
    weight: 2,
    rest: 1,
  },
  // Three from one side on eighth notes
  rush: {
    steps: [
      ['basic', 0, 0],
      ['basic', 0, 0.5],
      ['basic', 0, 0.5],
    ],
    fromLevel: 6,
    weight: 1,
    rest: 1,
  },
  // Strike one side while a kick chambers on the other
  kickCover: {
    steps: [
      ['basic', 1, 0],
      ['kicker', 0, 1],
    ],
    fromLevel: 8,
    weight: 2,
    rest: 1,
  },
  // Two quick strikes on the wrestler, then the other side
  grappleCover: {
    steps: [
      ['grappler', 0, 0],
      ['basic', 1, 1],
    ],
    fromLevel: 11,
    weight: 1,
    rest: 1,
  },
};

export const PATTERN = {
  /** Seconds between the two strikes of an "at once" step: one thumb can make both. */
  pincerGap: 0.22,
  /** Shortest gap between two strike moments. */
  minGap: 0.2,
  /** Extra seconds kept between two from the same side, beyond queueSpacing. */
  queueMargin: 0.05,
  /** Strike a blocker this long into its windup (its counter window), at base
   *  pace; as overtime shortens the windup, this shortens with it. */
  blockerReact: 0.15,
  /** Seconds per beat when a round has no tempo of its own. */
  defaultBeat: 0.6,
  /** A ramping round (Kumite) sends more patterns as it climbs: this much more
   *  share per level above its start, up to `rampMax`. */
  rampPerLevel: 0.02,
  rampMax: 0.5,
} as const;

/** Music tempo per stage (BPM), up a little each round; pattern beats follow it. */
export const STAGE_BPM = { dojo: 96, park: 108, rooftop: 120, perRound: 6 } as const;

/** The stage bosses: one ends the last exam of each stage. Each fights as a
 *  run of acts borrowed from the kinds the player has met, switching after
 *  each act's hits; his look is his own (BOSS_BUILDS in the renderer). */
export type BossId = 'senpai' | 'captain' | 'ronin';

/** A boss enters this long after the mat is clear, is knocked back this far
 *  between acts, and goes down in a longer slow motion (real seconds). */
export const BOSS = { entryDelay: 1.0, knockback: 1.6 * K, koSlowmo: 1.2 } as const;

export const BOSSES: Record<BossId, { acts: readonly EnemyKind[] }> = {
  senpai: { acts: ['basic', 'feinter', 'heavy', 'feinter', 'basic'] },
  captain: { acts: ['blocker', 'basic', 'feinter', 'heavy', 'blocker', 'basic'] },
  ronin: { acts: ['kicker', 'blocker', 'feinter', 'grappler', 'kicker', 'basic'] },
};
